import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { UserPlus, Lock } from "lucide-react";
import Button from "../UI/Button";

const MAX_AVATAR_BYTES = 5 * 1024 * 1024; // 5MB

const AddUserModal = ({
  isOpen,
  onClose,
  newUser,
  setNewUser,
  availablePermissions,
  togglePermission,
  handleCreateUser,
  loading,
  CONTROL,
  ACTION_BTN,
}) => {
  const [confirmPassword, setConfirmPassword] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setConfirmPassword("");
      setPreviewUrl("");
      setPhoneError("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [isOpen]);

  useEffect(() => {
    if (newUser?.avatarUrl) {
      setPreviewUrl(newUser.avatarUrl);
    } else {
      setPreviewUrl("");
    }
  }, [newUser?.avatarUrl]);

  useEffect(() => {
    return () => {
      try {
        if (previewUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(previewUrl);
        }
      } catch {}
    };
  }, [previewUrl]);

  const passwordMismatch = useMemo(() => {
    const p = (newUser?.password || "").trim();
    const c = (confirmPassword || "").trim();
    if (!p && !c) return false;
    return p !== c;
  }, [newUser?.password, confirmPassword]);

  if (!isOpen) return null;

  const setField = (key, value) => {
    setNewUser((prev) => ({ ...prev, [key]: value }));
  };

const validatePhone = (value) => {
  const phone = String(value || "").trim();
  if (!phone) return "";
  if (!/^(09\d{9}|\+639\d{9})$/.test(phone)) {
    return "Invalid phone number. Use 09XXXXXXXXX or +639XXXXXXXXX.";
  }
  return "";
};

  const handleAvatarPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type?.startsWith("image/")) {
      toast.error("Please upload an image file (JPG/PNG).");
      e.target.value = "";
      return;
    }

    if (file.size > MAX_AVATAR_BYTES) {
      toast.error("Image too large. Max 5MB.");
      e.target.value = "";
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";

      setPreviewUrl(result);

      setNewUser((prev) => ({
        ...prev,
        avatarFile: file,
        avatarUrl: result,
      }));
    };

    reader.onerror = () => {
      toast.error("Failed to load image preview.");
      e.target.value = "";
    };

    reader.readAsDataURL(file);

    e.target.value = "";
  };

  const removeAvatar = () => {
    setPreviewUrl("");
    setNewUser((prev) => ({
      ...prev,
      avatarFile: null,
      avatarUrl: "",
    }));

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const initials =
    `${newUser?.firstName?.[0] || ""}${newUser?.lastName?.[0] || ""}`.toUpperCase() || "?";

  const onCreate = () => {
    if (!newUser?.firstName?.trim() || !newUser?.lastName?.trim() || !newUser?.email?.trim() || !newUser?.role) {
      toast.error("Please fill in required fields (First Name, Last Name, Email, Role).");
      return;
    }

    if (!newUser?.password?.trim()) {
      toast.error("Please enter a password.");
      return;
    }

    if (passwordMismatch) {
      toast.error("Passwords do not match.");
      return;
    }

    const phoneValidationMessage = validatePhone(newUser?.phone || "");
    if (phoneValidationMessage) {
      setPhoneError(phoneValidationMessage);
      toast.error(phoneValidationMessage);
      return;
    }

    handleCreateUser();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 dark:bg-black/60 px-3 sm:px-4 py-6"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-3xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-5 sm:p-6 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-10 w-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-600 items-center justify-center shadow-md shrink-0">
                  <UserPlus className="h-4 w-4 text-white" />
                </span>
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                  Add New User
                </h2>
              </div>

              <p className="mt-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Create a new user account with appropriate role and permissions.
              </p>
            </div>

            <button onClick={onClose} className={ACTION_BTN} title="Close" type="button">
              <span className="text-xl leading-none text-gray-700 dark:text-gray-200">×</span>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 sm:p-6">
          <div className="space-y-5">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex flex-row md:flex-col items-center md:items-start gap-4 md:gap-3">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800"
                  />
                ) : (
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-950/30 dark:to-blue-950/20 flex items-center justify-center text-indigo-600 dark:text-indigo-200 text-xl sm:text-2xl font-semibold border border-indigo-100 dark:border-indigo-900/40 overflow-hidden">
                    {initials}
                  </div>
                )}

                <div className="flex flex-col gap-2 w-full md:w-auto">
                  <label className="inline-flex items-center justify-center px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors w-full md:w-auto">
                    <span>Upload Photo</span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleAvatarPick}
                    />
                  </label>

                  {previewUrl ? (
                    <button
                      type="button"
                      onClick={removeAvatar}
                      className="text-xs text-red-500 hover:text-red-600 text-left"
                    >
                      Remove photo
                    </button>
                  ) : null}

                  <p className="text-xs text-gray-500 dark:text-gray-400">JPG or PNG, max 5MB.</p>
                </div>
              </div>

              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    First Name
                  </label>
                  <input
                    type="text"
                    placeholder="Enter first name"
                    value={newUser?.firstName || ""}
                    onChange={(e) => setField("firstName", e.target.value)}
                    className={CONTROL}
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    Last Name
                  </label>
                  <input
                    type="text"
                    placeholder="Enter last name"
                    value={newUser?.lastName || ""}
                    onChange={(e) => setField("lastName", e.target.value)}
                    className={CONTROL}
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    Middle Name
                  </label>
                  <input
                    type="text"
                    placeholder="Enter middle name"
                    value={newUser?.middleName || ""}
                    onChange={(e) => setField("middleName", e.target.value)}
                    className={CONTROL}
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    Email Address
                  </label>
                  <input
                    type="email"
                    placeholder="Enter email address"
                    value={newUser?.email || ""}
                    onChange={(e) => setField("email", e.target.value)}
                    className={CONTROL}
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    Phone Number
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Enter phone number"
                    value={newUser?.phone || ""}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (/^\+?\d*$/.test(value)) {
                        setField("phone", value);
                        setPhoneError(validatePhone(value));
                      }
                    }}
                    onBlur={(e) => {
                      setPhoneError(validatePhone(e.target.value));
                    }}
                      onPaste={(e) => {
                        const pasted = e.clipboardData.getData("text");
                        if (!/^\+?\d*$/.test(pasted)) {
                          e.preventDefault();
                          setPhoneError("Invalid phone number. Use 09XXXXXXXXX or +639XXXXXXXXX.");
                          toast.error("Invalid phone number. Use 09XXXXXXXXX or +639XXXXXXXXX.");
                        }
                      }}
                    className={`${CONTROL} ${
                      phoneError ? "border-red-400 focus:ring-red-500 focus:border-red-500" : ""
                    }`}
                  />
                  {phoneError ? <p className="text-xs text-red-500">{phoneError}</p> : null}
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    Role
                  </label>
                  <select
                    value={newUser?.role || ""}
                    onChange={(e) => setField("role", e.target.value)}
                    className={CONTROL}
                  >
                    <option value="">Select role</option>
                    <option value="Staff">Staff</option>
                    <option value="Social Worker">Social Worker</option>
                    <option value="House Parent">House Parent</option>
                    <option value="Admin">Admin</option>
                  </select>
                </div>

                <div className="space-y-1.5 sm:col-span-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="password"
                      placeholder="Enter password"
                      value={newUser?.password || ""}
                      onChange={(e) => setField("password", e.target.value)}
                      className={`${CONTROL} pl-10`}
                    />
                  </div>
                </div>

                <div className="space-y-1.5 sm:col-span-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="password"
                      placeholder="Confirm password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`${CONTROL} pl-10 ${
                        passwordMismatch ? "border-red-400 focus:ring-red-500" : ""
                      }`}
                    />
                  </div>
                  {passwordMismatch ? (
                    <p className="text-xs text-red-500">Passwords do not match.</p>
                  ) : null}
                </div>
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Permissions{" "}
                <span className="font-normal text-gray-600 dark:text-gray-400">
                  (Optional - defaults will be applied based on role)
                </span>
              </p>

              {newUser?.role === "Admin" ? (
                <div className="mt-3">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200">
                    Full Access
                  </span>
                </div>
              ) : (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {availablePermissions.map((permission) => (
                    <label
                      key={permission}
                      className="inline-flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200"
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-700 text-indigo-600 focus:ring-indigo-500 bg-white dark:bg-gray-900"
                        checked={newUser?.permissions?.includes(permission)}
                        onChange={() => togglePermission(permission)}
                      />
                      <span className="break-words">{permission}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div
          className="p-5 sm:p-6 border-t border-gray-100 dark:border-gray-800 bg-white/95 dark:bg-gray-900/95 backdrop-blur"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 1rem)" }}
        >
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
              Cancel
            </Button>

            <Button
              onClick={onCreate}
              disabled={loading}
              loading={loading}
              className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              Create User
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUserModal;